// src/components/MonthlyReports.jsx
// Monthly Reports tab — upload source PDFs (Datto RMM / BCDR, Slipstream cyber),
// generate the client-ready pack, share a non-expiring link or PDF, and browse
// the archive by year → month → client. Self-contained; talks to the backend
// through src/api/monthlyReportsApi.js (MSAL token attached by apiClient).
import { useState, useEffect, useCallback, useRef } from "react";
import {
  uploadReport, generateReport, getArchive, getShareLink,
  getPdfAvailable, fetchPreviewHtml, downloadPdf, getPeriodStatus,
  draftSharedDidYouKnow, getSharedDidYouKnow, saveSharedDidYouKnow, clearSharedDidYouKnow,
  buildCredentialHygiene, getCredentialDetail, createCredentialLink, revokeCredentialLink,
  getIntakeQueue, getIntakeStatus, runIntake, fileIntakeItem, dismissIntakeItem, getIntakePdfUrl,
  fetchDiscussPending, markDiscussSeen,
  getEmailRecipients, getEmailDraft, sendReportEmail,
} from "../api/monthlyReportsApi";
import SectionNotesModal from "./reports/SectionNotesModal";
import RoadmapModal from "./reports/RoadmapModal";
import ReportNotices from "./reports/ReportNotices";
import { runBulkGenerate } from "./reports/bulkGenerate";
import { visibleReportClients, REPORTED_TIERS } from "./reports/visibleClients";

// BOTH dark-web spellings are keyed on purpose. The extractor type is
// "dark-web"; the blob key — and therefore everything the archive and
// period-status endpoints report, since they read the type out of the blob
// NAME — is "darkweb". Keying only the hyphenated form left the archive
// rendering a grey chip reading the raw "darkweb" instead of a purple
// "Dark Web" one. Until the blob key is migrated, the UI has to accept both.
const TYPE_LABEL = {
  "datto-rmm": "Datto RMM", "datto-bcdr": "Datto BCDR", "slipstream-cyber": "Cyber",
  "dark-web": "Dark Web", darkweb: "Dark Web",
  // The one non-PDF source. Its blob key and its extractor type are the same
  // string, so unlike dark web it needs only one spelling.
  "3cx-call-report": "Phone",
};
const TYPE_COLOR = {
  "datto-rmm": "#2a78d6", "datto-bcdr": "#1baf7a", "slipstream-cyber": "#4a3aa7",
  "dark-web": "#7a3d8f", darkweb: "#7a3d8f",
  "3cx-call-report": "#0e2e3e",
};
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// The four badges on each client card. `key` is what period-status reports.
// `live: true` = comes from an API, not an upload, so the badge means "the
// integration can see this client" rather than "a PDF was uploaded".
const SOURCES = [
  { key: "slipstream-cyber", label: "Cyber", color: "#4a3aa7" },
  { key: "datto-bcdr", label: "Backup", color: "#1baf7a" },
  { key: "datto-rmm", label: "IT Health", color: "#2a78d6", live: true },
  { key: "wifi", label: "WiFi", color: "#0f8dbd", live: true },
  // Blob key is "darkweb" (no hyphen); the extractor type is "dark-web".
  { key: "darkweb", label: "Dark Web", color: "#7a3d8f" },
];

// Reports are written for the month just gone, so that is the default.
function previousPeriod() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function periodLabel(p) {
  const m = /^(\d{4})-(\d{2})$/.exec(p || "");
  return m ? `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : (p || "");
}
const wordsOf = (s) => String(s || "").trim().split(/\s+/).filter(Boolean).length;
// Tolerant client-name comparison for the mismatch warning only — never used to
// choose a client (the card the file was dropped on decides that).
function looseNameMatch(a, b) {
  const n = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const x = n(a), y = n(b);
  if (!x || !y) return true;
  return x === y || (x.length >= 6 && y.length >= 6 && (x.includes(y) || y.includes(x)));
}

// small inline UI helpers ------------------------------------------------------
const card = { background: "var(--card,#fff)", border: "1px solid var(--border,#e2e2e2)", borderRadius: 12, padding: 16 };

// ── "Email report link to client" helpers ──────────────────────────────────
// Same rule as reportEmailService.EMAIL_RE on the server, which validates
// again on send; this copy exists so an invalid CC blocks the send BEFORE the
// round-trip and names the offending entry.
const EMAIL_RE = /^[^\s@,;<>()[\]"]+@[^\s@,;<>()[\]"]+\.[A-Za-z]{2,}$/;
const isEmail = (s) => EMAIL_RE.test(String(s || "").trim());
function parseCc(text) {
  const valid = [], invalid = [], seen = new Set();
  String(text || "").split(/[,\n;]/).forEach((raw) => {
    const v = raw.trim();
    if (!v) return;
    if (!isEmail(v)) { invalid.push(v); return; }
    const k = v.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k); valid.push(v);
  });
  return { valid, invalid };
}
const CC_STORAGE_KEY = "msp-os-report-email-cc";
const readStoredCc = () => { try { return localStorage.getItem(CC_STORAGE_KEY) || ""; } catch { return ""; } };
const btn = (primary) => ({
  padding: "7px 13px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--border,#d0d0d0)",
  background: primary ? "var(--dt-secondary,#0b80a5)" : "var(--card,#fff)",
  color: primary ? "#fff" : "var(--text,#222)",
});
function TypeBadge({ t }) {
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, color: "#fff", background: TYPE_COLOR[t] || "#888" }}>{TYPE_LABEL[t] || t}</span>;
}

// Source badge: coloured when that source is present for the client+month,
// grey outline when it is still missing. The badge IS the upload status — the
// uploaded files themselves are deliberately never listed.
function SourceBadge({ src, on }) {
  return (
    <span title={src.live
      ? (on ? `${src.label} — live from the API` : `${src.label} — the integration can't see this client yet`)
      : (on ? `${src.label} — uploaded` : `${src.label} — not uploaded yet`)}
      style={{
        fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5,
        color: on ? "#fff" : "var(--text3,#9a9a9a)",
        background: on ? src.color : "transparent",
        border: on ? "1px solid transparent" : "1px dashed var(--border,#cfcfcf)",
      }}>{src.label}</span>
  );
}

// Named credential table — rendered ONLY inside the authenticated modal.
// Deliberately a separate component so it can never be pulled into anything that
// feeds the client's shared report.
const PW_CHIP = { weak: ["#fdeeee", "#c0392b", "Weak"], ok: ["#fff4e2", "#b7791f", "OK"], strong: ["#eefaef", "#1e7d34", "Strong"], "N/A": ["#f2f2f2", "#888", "Not documented"] };
const MFA_CHIP = {
  strong: ["#eefaef", "#1e7d34", "Strong"],
  "sms-voice-only": ["#fff4e2", "#b7791f", "SMS/voice only"],
  none: ["#fdeeee", "#c0392b", "None"],
  "pending-first-signin": ["#f2f2f2", "#888", "Awaiting 1st sign-in"],
  unavailable: ["#f2f2f2", "#888", "Not available"],
};
function Chip({ spec }) {
  const c = spec || MFA_CHIP.unavailable;
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: c[0], color: c[1], whiteSpace: "nowrap" }}>{c[2]}</span>;
}
function CredTable({ data }) {
  const a = data.aggregate || {}, pw = a.pw || {}, mfa = a.mfa || {};
  return (
    <>
      <div style={{ fontSize: 12, marginBottom: 10 }}>
        <strong>{a.mailboxes}</strong> licensed mailboxes · passwords: {pw.weak || 0} weak, {pw.ok || 0} ok, {pw.strong || 0} strong, {pw.notDocumented || 0} not documented
        <br />MFA: {mfa.strong || 0} strong{mfa.smsVoiceOnly ? `, ${mfa.smsVoiceOnly} SMS/voice only` : ""}{mfa.none ? `, ${mfa.none} none` : ""}{mfa.pendingFirstSignIn ? `, ${mfa.pendingFirstSignIn} awaiting first sign-in` : ""}
      </div>
      {!!mfa.smsVoiceOnly && (
        <div style={{ fontSize: 11.5, background: "#fff4e2", color: "#8a5a12", padding: "8px 11px", borderRadius: 8, marginBottom: 10 }}>
          📱 {mfa.smsVoiceOnly} on SMS/voice only — must move to a stronger method before Microsoft retires it on 1 Feb 2027.
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          {["Email", "User", "Password", "MFA"].map((h) => (
            <th key={h} style={{ textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: ".4px", color: "var(--text3,#999)", padding: "0 8px 5px 0" }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {(data.users || []).map((u) => (
            <tr key={u.email}>
              <td style={{ fontSize: 11.5, padding: "5px 8px 5px 0", borderTop: "1px solid var(--border,#eee)" }}>{u.email}</td>
              <td style={{ fontSize: 11.5, padding: "5px 8px 5px 0", borderTop: "1px solid var(--border,#eee)" }}>{u.displayName || ""}</td>
              <td style={{ padding: "5px 8px 5px 0", borderTop: "1px solid var(--border,#eee)" }}><Chip spec={PW_CHIP[u.pwRating] || PW_CHIP["N/A"]} /></td>
              <td style={{ padding: "5px 0", borderTop: "1px solid var(--border,#eee)" }}><Chip spec={MFA_CHIP[u.mfa]} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export default function MonthlyReports({ companies = [], managedCompanyIds = null, onCreateQuote = null }) {
  const [view, setView] = useState("generate");       // "generate" | "archive"
  const [period, setPeriod] = useState(previousPeriod());
  const [status, setStatus] = useState(null);         // period-status: {clients:{cid:{types,wifi,...}}}
  const [tierFilter, setTierFilter] = useState("All");   // plan filter — see cardCompanies
  const [statusLoading, setStatusLoading] = useState(false);
  const [busyCid, setBusyCid] = useState(null);       // card mid-upload/generate
  const [genByCid, setGenByCid] = useState({});       // cid -> generate result
  const [generating, setGenerating] = useState(false);
  const [pdfAvailable, setPdfAvailable] = useState(false);
  // ── Email report link ──
  const [recipients, setRecipients] = useState({});        // { [cid]: email } — the stored per-client To
  const [ccText, setCcText] = useState(readStoredCc);       // page-level CC, comma-separated, remembered per browser
  const [emailDlg, setEmailDlg] = useState(null);           // { cid, name, loading, draft, to, sending, error, sent }
  const [preview, setPreview] = useState(null);       // {title, html, companyId, period}
  // Export-to-PDF from inside the viewer: a server round-trip (DocRaptor), so
  // the button disables while it runs and a failure is shown IN the toolbar —
  // the toast lives in the page beneath this overlay and can be hidden by it.
  const [previewPdfBusy, setPreviewPdfBusy] = useState(false);
  const [previewPdfError, setPreviewPdfError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Files the server could not type on its own. Holding the File object lets
  // the same bytes be re-sent with an explicit type — no re-picking the file.
  const [typePrompt, setTypePrompt] = useState(null); // {cid, companyName, items:[{file, reason, error, types, choice}]}
  const [typeBusy, setTypeBusy] = useState(false);
  // Section notes, scoped to one client + the selected month.
  const [notesFor, setNotesFor] = useState(null);   // {companyId, companyName}
  const [roadmapFor, setRoadmapFor] = useState(null); // {companyId, companyName}
  // What the CLIENT ticked "Discuss at our next catch-up" on their report.
  const [discuss, setDiscuss] = useState([]);       // [{companyId, period, count, unread, sections}]
  const [archive, setArchive] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [msg, setMsg] = useState(null);               // {kind,text}
  const dropRef = useRef({});                         // cid -> hidden <input file>
  // ── "Did You Know?" — approve-first, so a draft is only ever local state
  //    until Save writes it to the frozen per-client+month blob.
  // ── Did You Know? — ONE per month, shared by every client's report ────────
  // Keyed by period, never by client. `dykSaved` is what is currently stored
  // server-side; `dykText` is the editable draft, which may differ from it.
  const [dykText, setDykText] = useState("");
  const [dykTopic, setDykTopic] = useState(null);
  const [dykSaved, setDykSaved] = useState(null);     // the stored record, or null
  const [dykBusy, setDykBusy] = useState(false);
  const [dykOpen, setDykOpen] = useState(false);      // editor expanded
  // Credential hygiene — the NAMED tier. Kept in a modal, never rendered into
  // anything that reaches the client's share link.
  const [credOpen, setCredOpen] = useState(null);     // {companyId, companyName, data}
  const [credBusy, setCredBusy] = useState(false);
  const [credLink, setCredLink] = useState(null);     // {url, jti, expiresAt}
  // ── Inbound reports — vendor PDFs that arrived by email and could not be
  //    filed with confidence. Held, never guessed at.
  const [intake, setIntake] = useState(null);         // queue items
  const [intakeInfo, setIntakeInfo] = useState(null); // mailbox status
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeBusy, setIntakeBusy] = useState(null); // hash mid-action
  const [edits, setEdits] = useState({});             // hash -> {companyId,type,period}

  // Cards are the MANAGED clients (Client Register "Mgd"). Fall back to every
  // company only if the managed set hasn't loaded, so the grid is never empty.
  const managedSet = managedCompanyIds instanceof Set ? managedCompanyIds
    : Array.isArray(managedCompanyIds) ? new Set(managedCompanyIds.map(String)) : null;
  /* ── PLAN FILTER ───────────────────────────────────────────────────────────
     ⛔ ONLY Bronze/Silver/Gold GET A MONTHLY REPORT. Copper and Adhoc are absent
     from REPORTED_TIERS entirely — not an empty filter option — because "this
     plan has no report" and "this plan's report is empty" are different things
     and only the first is true.
     ⚠ `_tier` here is the RESOLVED tier from /companies-with-tiers: Craig's
     manual override where he has set one, the contract-name derivation
     otherwise. So as he works through the ~82 un-set clients they move into the
     right filter on their own, with no second place to maintain. */
  /* ⛔ The cards AND the "Generate all" button read this ONE list, so the count
     on the button and the set it generates cannot drift apart. The rule itself
     lives in reports/visibleClients.js where it is unit-tested — this component
     cannot be imported by a test (MSAL dies on load). */
  const cardCompanies = visibleReportClients(companies, { managedSet, tierFilter });
  const nameFor = useCallback((id) => (companies.find((c) => String(c.id) === String(id))?.companyName) || `Company ${id}`, [companies]);
  const toast = useCallback((text, kind = "ok") => { setMsg({ kind, text }); setTimeout(() => setMsg(null), kind === "err" ? 8000 : 3500); }, []);

  useEffect(() => { getPdfAvailable().then((r) => setPdfAvailable(!!r.pdfAvailable)).catch(() => {}); }, []);
  useEffect(() => { getEmailRecipients().then((r) => setRecipients(r.data || {})).catch(() => {}); }, []);
  useEffect(() => { try { localStorage.setItem(CC_STORAGE_KEY, ccText); } catch { /* private mode */ } }, [ccText]);
  // Client discuss flags. Non-fatal on failure — a missing badge must never
  // stop the reports tab loading; the flags are still on the prep either way.
  useEffect(() => { fetchDiscussPending().then(setDiscuss).catch(() => {}); }, []);

  // Changing the month repopulates every card.
  const loadStatus = useCallback((per) => {
    if (!per) return Promise.resolve();
    setStatusLoading(true);
    return getPeriodStatus(per)
      .then((r) => setStatus(r))
      .catch((e) => toast(e?.response?.data?.error || e.message || "Could not load month status", "err"))
      .finally(() => setStatusLoading(false));
  }, [toast]);
  useEffect(() => { if (view === "generate") loadStatus(period); }, [view, period, loadStatus]);

  // The per-client industry map is no longer read here: the shared snippet
  // covers every client, so there is no single industry to theme it to. The
  // map is still edited in Settings -> Client Register and is still used by the
  // legacy per-client draft route.

  // Load the month's snippet whenever the period changes.
  //
  // This is what was missing before: the per-client version only fetched when
  // the card's panel was OPENED, so on every page load and every re-render the
  // UI showed "none yet" for a snippet that was sitting in storage, already
  // approved. It was persisted the whole time — nothing ever read it back.
  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(period)) return;
    let cancelled = false;
    setDykBusy(true);
    getSharedDidYouKnow(period)
      .then((r) => {
        if (cancelled) return;
        setDykSaved(r || null);
        setDykText(r?.text || "");
        setDykTopic(r?.topic || null);
      })
      .catch(() => { if (!cancelled) { setDykSaved(null); setDykText(""); setDykTopic(null); } })
      .finally(() => { if (!cancelled) setDykBusy(false); });
    return () => { cancelled = true; };
  }, [period]);

  // Draft (or re-draft). Nothing is persisted — it lands in the textarea to be
  // edited. Regenerate passes the topic in play plus the stored one so the angle
  // actually changes rather than circling the same theme.
  const doDraftDyk = useCallback(() => {
    setDykBusy(true);
    const avoid = [dykTopic, dykSaved?.topic].filter(Boolean);
    draftSharedDidYouKnow(period, { avoidTopics: avoid })
      .then((r) => { setDykText(r.text || ""); setDykTopic(r.topic || null); toast("Draft ready — edit it, then Save."); })
      .catch((e) => toast(e?.response?.data?.error || e.message || "Could not draft a snippet", "err"))
      .finally(() => setDykBusy(false));
  }, [period, dykTopic, dykSaved, toast]);

  const doSaveDyk = useCallback(() => {
    const text = dykText.trim();
    if (!text) return toast("Nothing to save.", "err");
    setDykBusy(true);
    saveSharedDidYouKnow(period, { text, topic: dykTopic })
      .then((rec) => { setDykSaved(rec); toast(`Saved — every ${periodLabel(period)} report will carry it.`); })
      .catch((e) => toast(e?.response?.data?.error || e.message || "Save failed", "err"))
      .finally(() => setDykBusy(false));
  }, [dykText, dykTopic, period, toast]);

  const doClearDyk = useCallback(() => {
    setDykBusy(true);
    clearSharedDidYouKnow(period)
      .then(() => { setDykSaved(null); setDykText(""); setDykTopic(null); toast("Removed — no panel on this month's reports."); })
      .catch((e) => toast(e?.response?.data?.error || e.message || "Could not remove", "err"))
      .finally(() => setDykBusy(false));
  }, [period, toast]);

  const openCredentials = useCallback((cid, name) => {
    setCredBusy(true); setCredLink(null);
    setCredOpen({ companyId: cid, companyName: name, data: null });
    getCredentialDetail(cid, period)
      .then((d) => setCredOpen({ companyId: cid, companyName: name, data: d }))
      .catch(() => setCredOpen({ companyId: cid, companyName: name, data: null }))
      .finally(() => setCredBusy(false));
  }, [period]);

  const runCredentialBuild = useCallback((cid, name) => {
    setCredBusy(true);
    buildCredentialHygiene(cid, period)
      .then(() => getCredentialDetail(cid, period))
      .then((d) => { setCredOpen({ companyId: cid, companyName: name, data: d }); toast("Credential check complete."); })
      .catch((e) => toast(e?.response?.data?.error || e.message || "Could not run the credential check", "err"))
      .finally(() => setCredBusy(false));
  }, [period, toast]);

  const statusFor = useCallback((cid) => {
    const e = status?.clients?.[String(cid)] || {};
    const types = e.types || [];
    // Derived FROM SOURCES, never hand-listed. A hand-written map is exactly how
    // the Dark Web badge shipped dead: the source was added to SOURCES (with a
    // comment about the darkweb/dark-web spelling, so the author was looking
    // right at the hazard) and never added here. `has.darkweb` was therefore
    // undefined, the badge read falsy, and it could not light no matter what was
    // filed — the report WAS being stored and extracted correctly the whole time.
    // Adding a sixth source now cannot repeat it.
    const has = {};
    // wifi is the one source that is not a report type: it comes from the UniFi
    // sync, not from an uploaded/extracted blob.
    SOURCES.forEach((s) => { has[s.key] = s.key === "wifi" ? !!e.wifi : types.includes(s.key); });
    return {
      has,
      unifiSiteName: e.unifiSiteName || null,
      generated: !!e.generated,
      count: types.length,
    };
  }, [status]);

  const loadArchive = useCallback(() => {
    setArchiveLoading(true);
    getArchive().then((r) => setArchive(r)).catch((e) => toast(e.message || "Archive failed", "err")).finally(() => setArchiveLoading(false));
  }, [toast]);
  useEffect(() => { if (view === "archive" && !archive) loadArchive(); }, [view, archive, loadArchive]);

  // ── per-client upload ──────────────────────────────────────────────────────
  // Files are scoped to the card they were dropped on. We do NOT list them —
  // the source badges flipping coloured is the confirmation. The classifier
  // still reads the client/month off the PDF, and we warn on a mismatch rather
  // than silently filing someone else's report against this client.
  const handleFiles = async (cid, companyName, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusyCid(cid);
    const warnings = [];
    const needType = [];
    let landed = 0;
    for (const file of files) {
      try {
        const r = await uploadReport(file, { period, companyId: cid, confirm: "true" });
        if (r && r.ok) {
          landed++;
          const sameClient = !r.detectedClient || looseNameMatch(r.detectedClient, companyName);
          if (!sameClient) warnings.push(`"${file.name}" looks like ${r.detectedClient} but was filed under ${companyName}`);
          if (r.detectedPeriod && r.detectedPeriod !== period) {
            warnings.push(`"${file.name}" is dated ${periodLabel(r.detectedPeriod)} but was filed under ${periodLabel(period)}`);
          }
        } else if (r && r.needsType) {
          needType.push({ file, reason: r.reason, error: r.error, types: r.types || [], choice: "" });
        } else {
          warnings.push(`"${file.name}": ${(r && (r.reason || r.error)) || "could not identify the report"}`);
        }
      } catch (e) {
        // A 422 arrives as a thrown axios error, so the "pick the type
        // yourself" case has to be recognised here too — not only on the 202
        // path above. Missing this is what would leave the safety valve
        // reachable in theory and never in practice.
        const d = e?.response?.data;
        if (d?.needsType) needType.push({ file, reason: d.reason, error: d.error, types: d.types || [], choice: "" });
        else warnings.push(`"${file.name}": ${d?.error || d?.reason || e.message || "upload failed"}`);
      }
    }
    await loadStatus(period);
    setBusyCid(null);
    if (needType.length) setTypePrompt({ cid, companyName, items: needType });
    if (warnings.length) toast(`⚠ ${warnings.join(" · ")}`, "err");
    else if (landed) toast(`${landed} report${landed > 1 ? "s" : ""} filed for ${companyName}.`);
  };

  // Re-send the same bytes with the type stated explicitly. Only the TYPE is
  // forced — the client and the duplicate-hash guards run again server-side, so
  // naming the type cannot be used to push a file onto the wrong client.
  const submitTypeChoices = async () => {
    if (!typePrompt || typeBusy) return;
    const chosen = typePrompt.items.filter((it) => it.choice);
    if (!chosen.length) return;
    setTypeBusy(true);
    const warnings = [];
    let landed = 0;
    for (const it of chosen) {
      try {
        const r = await uploadReport(it.file, {
          period, companyId: typePrompt.cid, type: it.choice, confirm: "true",
        });
        if (r && r.ok) landed++;
        else warnings.push(`"${it.file.name}": ${(r && (r.error || r.reason)) || "could not file"}`);
      } catch (e) {
        const d = e?.response?.data;
        warnings.push(`"${it.file.name}": ${d?.error || d?.reason || e.message || "upload failed"}`);
      }
    }
    await loadStatus(period);
    setTypeBusy(false);
    setTypePrompt(null);
    if (warnings.length) toast(`⚠ ${warnings.join(" · ")}`, "err");
    else if (landed) toast(`${landed} report${landed > 1 ? "s" : ""} filed for ${typePrompt.companyName}.`);
  };

  // ── report actions (shared by generate + archive) ───────────────────────────
  const doGenerate = async (cid) => {
    if (!cid || !period) return;
    setGenerating(true); setBusyCid(cid);
    try {
      const r = await generateReport(cid, period);
      setGenByCid((g) => ({ ...g, [cid]: r }));
      await loadStatus(period);
      toast(`Report generated for ${nameFor(cid)}.`);
    } catch (e) {
      toast(e?.response?.data?.error || e.message || "Generate failed", "err");
    } finally { setGenerating(false); setBusyCid(null); }
  };

  /* ── BULK GENERATE — the same doGenerate action, looped ────────────────────
     ⛔ It calls `generateReport(cid, period)`, which IS what the card's
     Regenerate button calls. There is no bulk endpoint and there must not be
     one: a pack produced here has to be byte-for-byte the pack Regenerate
     produces, including the roadmap/tier/notice freezes the route performs.
     The per-client UI chrome (a toast each, a status reload each) is
     deliberately NOT reused — N toasts is noise and N status reloads is N
     wasted round trips, so the refresh happens once at the end. */
  const [bulk, setBulk] = useState(null);   // {running,done,total,current,results,cancelled}
  const cancelBulkRef = useRef(false);

  const doBulkGenerate = async () => {
    const targets = cardCompanies;
    if (!targets.length || !period) return;
    /* ⚠ Regenerating overwrites this month's pack and RE-FREEZES its snapshots,
       exactly as the per-client button does — so it is confirmed, and the
       wording says what it does rather than just how long it takes. */
    const ok = window.confirm(
      `Generate/regenerate reports for ${targets.length} visible client${targets.length === 1 ? "" : "s"}?

`
      + `This refreshes each client's pack for ${periodLabel(period)} and may take several minutes.`
    );
    if (!ok) return;

    cancelBulkRef.current = false;
    setBulk({ running: true, done: 0, total: targets.length, current: null, results: [], cancelled: false });

    const out = await runBulkGenerate(
      targets,
      (cid) => generateReport(cid, period),
      {
        shouldCancel: () => cancelBulkRef.current,
        onProgress: ({ index, total, client }) =>
          setBulk((b) => ({ ...b, done: index, total, current: client.companyName })),
        onResult: (r) =>
          setBulk((b) => ({ ...b, done: b.done + 1, results: [...b.results, r] })),
      }
    );

    setBulk({ running: false, done: out.results.length, total: targets.length, current: null,
      results: out.results, cancelled: out.cancelled });
    /* One refresh for the whole run — every card's badge moves at once. */
    await loadStatus(period);
    if (out.failed) toast(`${out.ok} generated, ${out.failed} failed — see the summary below.`, "err");
    else if (out.cancelled) toast(`Stopped after ${out.ok}.`);
    else toast(`${out.ok} report${out.ok === 1 ? "" : "s"} generated.`);
  };

  const doPreview = async (cid, per, title) => {
    setPreviewLoading(true);
    try {
      const html = await fetchPreviewHtml(cid, per);
      setPreviewPdfError(null);
      setPreview({ title: title || `${nameFor(cid)} — ${periodLabel(per)}`, html, companyId: cid, period: per });
    } catch (e) {
      toast(e?.response?.data?.error || e.message || "Preview failed", "err");
    } finally { setPreviewLoading(false); }
  };

  const doShare = async (cid, per) => {
    try {
      const r = await getShareLink(cid, per);
      if (r.shareUrl) {
        try { await navigator.clipboard.writeText(r.shareUrl); toast("Share link copied — opening…"); }
        catch { toast("Share link ready — opening…"); }
        window.open(r.shareUrl, "_blank", "noopener");
      }
    } catch (e) { toast(e?.response?.data?.error || e.message || "Share link failed", "err"); }
  };

  const doPdf = async (cid, per) => {
    try {
      toast("Building PDF…");
      await downloadPdf(cid, per, `${(nameFor(cid) || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-monthly-report-${per}.pdf`);
      return null;
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "PDF export failed";
      toast(msg, "err");
      return msg;
    }
  };

  // ── Email report link to client ──────────────────────────────────────────
  // Opens the confirm dialog with the server-composed draft: To pre-filled from
  // the stored recipient (editable), CC from the page field, the exact subject,
  // wording and link that will go out. Nothing is sent until "Send".
  // `test` = the [TEST]-marked variant: the server prefixes the subject, opens
  // the body with a marker line, and does NOT remember the To as the client's
  // recipient. Toggling it re-fetches the draft so the preview is exactly what
  // will go out — the wording is the server's, never rebuilt here.
  const loadEmailDraft = async (cid, test) => {
    try {
      const draft = await getEmailDraft(cid, period, { test });
      setEmailDlg((d) => d && d.cid === cid ? { ...d, loading: false, draft, to: d.to || draft.to || "" } : d);
    } catch (e) {
      setEmailDlg((d) => d && d.cid === cid ? { ...d, loading: false, error: e?.response?.data?.error || e.message || "Could not prepare the email" } : d);
    }
  };
  const openEmailDialog = async (cid) => {
    const name = nameFor(cid);
    setEmailDlg({ cid, name, loading: true, draft: null, to: recipients[cid] || "", test: false, sending: false, error: null, sent: null });
    await loadEmailDraft(cid, false);
  };
  const toggleEmailTest = async (test) => {
    if (!emailDlg || emailDlg.sending) return;
    setEmailDlg((d) => ({ ...d, test, loading: true, draft: null, error: null }));
    await loadEmailDraft(emailDlg.cid, test);
  };
  const doSendEmail = async () => {
    if (!emailDlg || emailDlg.sending || !emailDlg.draft) return;
    const to = String(emailDlg.to || "").trim();
    const cc = parseCc(ccText);
    if (!isEmail(to)) return setEmailDlg((d) => ({ ...d, error: to ? `"${to}" is not a valid email address` : "A To address is required" }));
    if (cc.invalid.length) return setEmailDlg((d) => ({ ...d, error: `CC contains an invalid address: ${cc.invalid.join(", ")}` }));
    setEmailDlg((d) => ({ ...d, sending: true, error: null }));
    try {
      const out = await sendReportEmail(emailDlg.cid, period, { to, cc: cc.valid, test: !!emailDlg.test });
      if (out.remembered) setRecipients((r) => ({ ...r, [emailDlg.cid]: out.to }));
      setEmailDlg((d) => ({ ...d, sending: false, sent: out }));
      toast(`${out.test ? "Test email" : "Report link"} sent to ${out.to}${out.cc?.length ? ` (cc ${out.cc.join(", ")})` : ""}`);
    } catch (e) {
      setEmailDlg((d) => ({ ...d, sending: false, error: e?.response?.data?.error || e.message || "Send failed" }));
    }
  };

  // The viewer's "Export to PDF" — the SAME DocRaptor route, auth and filename
  // as the per-client ⬇ buttons (it calls doPdf), not a browser print.
  const doPreviewPdf = async () => {
    if (!preview || previewPdfBusy) return;
    setPreviewPdfBusy(true);
    setPreviewPdfError(null);
    try {
      const err = await doPdf(preview.companyId, preview.period);
      if (err) setPreviewPdfError(err);
    } finally { setPreviewPdfBusy(false); }
  };

  // ── Inbound reports ──────────────────────────────────────────────────────
  const loadIntake = useCallback(() => {
    setIntakeLoading(true);
    return Promise.all([getIntakeQueue(), getIntakeStatus().catch(() => null)])
      .then(([items, info]) => {
        setIntake(items);
        setIntakeInfo(info);
        // Seed each row's editable fields from the backend's best guess.
        setEdits((prev) => {
          const next = { ...prev };
          for (const it of items) {
            if (!next[it.hash]) {
              next[it.hash] = {
                companyId: it.companyId ? String(it.companyId) : "",
                type: it.type || "",
                period: it.period || "",
              };
            }
          }
          return next;
        });
      })
      .catch((e) => toast(e?.response?.data?.error || e.message || "Could not load inbound reports", "err"))
      .finally(() => setIntakeLoading(false));
  }, [toast]);

  useEffect(() => { if (view === "intake" && !intake) loadIntake(); }, [view, intake, loadIntake]);
  // The pending count drives the tab badge, so it is fetched once on mount too.
  useEffect(() => { getIntakeQueue().then(setIntake).catch(() => {}); }, []);

  const patchEdit = (hash, k, v) => setEdits((e) => ({ ...e, [hash]: { ...(e[hash] || {}), [k]: v } }));

  const confirmIntake = (item) => {
    const e = edits[item.hash] || {};
    if (!e.companyId || !e.type || !e.period) { toast("Pick a client, type and month first.", "err"); return; }
    setIntakeBusy(item.hash);
    fileIntakeItem(item.hash, { companyId: e.companyId, type: e.type, period: e.period })
      .then((r) => {
        toast(`Filed to ${r.companyName} — ${periodLabel(r.period)}`);
        return loadIntake().then(() => { if (view === "generate") loadStatus(period); });
      })
      // A 409 here is the safety guard refusing the client that was chosen —
      // surface its reason verbatim rather than a generic failure.
      .catch((err) => toast(err?.response?.data?.error || err.message || "Could not file this report", "err"))
      .finally(() => setIntakeBusy(null));
  };

  const rejectIntake = (item) => {
    if (!window.confirm(`Discard "${item.fileName}"? The held file is deleted too.`)) return;
    setIntakeBusy(item.hash);
    dismissIntakeItem(item.hash)
      .then(() => { toast("Discarded."); return loadIntake(); })
      .catch((err) => toast(err?.response?.data?.error || err.message || "Could not discard", "err"))
      .finally(() => setIntakeBusy(null));
  };

  const checkMailNow = () => {
    setIntakeLoading(true);
    runIntake({ dryRun: false })
      .then((r) => {
        if (!r.ok) { toast(r.error || "Intake did not run", "err"); return null; }
        toast(`Checked ${intakeInfo?.mailbox || "the mailbox"} — ${r.filed} filed, ${r.review} to review`);
        return loadIntake().then(() => { if (view === "generate") loadStatus(period); });
      })
      .catch((e) => toast(e?.response?.data?.error || e.message || "Could not check the mailbox", "err"))
      .finally(() => setIntakeLoading(false));
  };

  const pendingCount = Array.isArray(intake) ? intake.length : 0;

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div className="panel" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto", padding: 20, gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Monthly Reports</h2>
        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          <button style={{ ...btn(view === "generate"), borderRadius: "7px 0 0 7px" }} onClick={() => setView("generate")}>Generate</button>
          <button style={{ ...btn(view === "intake"), borderRadius: 0 }} onClick={() => setView("intake")}>
            Inbound
            {pendingCount > 0 && (
              <span style={{ marginLeft: 6, background: view === "intake" ? "rgba(255,255,255,0.25)" : "#c0392b",
                color: "#fff", borderRadius: 9, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>{pendingCount}</span>
            )}
          </button>
          <button style={{ ...btn(view === "notices"), borderRadius: 0 }} onClick={() => setView("notices")}>Notices</button>
          <button style={{ ...btn(view === "archive"), borderRadius: "0 7px 7px 0" }} onClick={() => setView("archive")}>Archive</button>
        </div>
      </div>

      {msg && (
        <div style={{ padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
          background: msg.kind === "err" ? "#fdeeee" : "#eefaef", color: msg.kind === "err" ? "#c0392b" : "#1e7d34" }}>{msg.text}</div>
      )}

      {/* ── BULK RUN: progress while running, per-client ✓/✗ when done ──────
          ⛔ EVERY CLIENT IS LISTED, pass or fail. A bulk action that reports
          only a total is how a client that failed to generate gets sent last
          month's pack — the failure has to be on screen, with the server's own
          reason beside it. */}
      {view === "generate" && bulk && (
        <div style={{ ...card, borderLeft: `3px solid ${bulk.running ? "var(--strat)" : bulk.results.some((r) => !r.ok) ? "var(--danger)" : "var(--ops)"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: bulk.results.length ? 8 : 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              {bulk.running
                ? `Generating ${bulk.done} of ${bulk.total}${bulk.current ? ` — ${bulk.current}` : ""}…`
                : `${bulk.results.filter((r) => r.ok).length} generated`
                  + (bulk.results.some((r) => !r.ok) ? `, ${bulk.results.filter((r) => !r.ok).length} failed` : "")
                  + (bulk.cancelled ? " · stopped early" : "")}
            </div>
            {!bulk.running && (
              <button className="btn" style={{ fontSize: 11, marginLeft: "auto" }} onClick={() => setBulk(null)}>Dismiss</button>
            )}
          </div>
          {bulk.running && (
            <div style={{ height: 4, background: "var(--border)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.round((bulk.done / Math.max(1, bulk.total)) * 100)}%`, background: "var(--strat)" }} />
            </div>
          )}
          {bulk.results.length > 0 && (
            <div style={{ maxHeight: 220, overflowY: "auto", fontSize: 12 }}>
              {bulk.results.map((r) => (
                <div key={r.companyId} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "3px 0", borderTop: "1px solid var(--border)" }}>
                  <span style={{ color: r.ok ? "var(--ops)" : "var(--danger)", fontWeight: 700 }}>{r.ok ? "✓" : "✗"}</span>
                  <span style={{ minWidth: 190 }}>{r.companyName || `Company ${r.companyId}`}</span>
                  {!r.ok && <span style={{ color: "var(--danger)" }}>{r.error}</span>}
                  {r.ok && r.retried > 0 && <span style={{ color: "var(--text3)" }}>after {r.retried} retr{r.retried === 1 ? "y" : "ies"}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "generate" && (
        <>
          <div style={{ ...card, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 12 }}>
              <div style={{ color: "var(--text3,#888)", marginBottom: 4, fontWeight: 600 }}>Reporting month</div>
              <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid var(--border,#d0d0d0)", fontSize: 13 }} />
            </label>
            {/* Page-level CC for "Email report to client". Comma-separated; parsed,
                validated and deduped on send (and again on the server). Empty
                means the email goes with NO CC header at all. */}
            <label style={{ fontSize: 12, flex: "1 1 260px", minWidth: 220, maxWidth: 520 }}>
              <div style={{ color: "var(--text3,#888)", marginBottom: 4, fontWeight: 600 }}>CC on report emails <span style={{ fontWeight: 400 }}>(comma-separated, optional)</span></div>
              <input type="text" value={ccText} onChange={(e) => setCcText(e.target.value)} placeholder="e.g. accounts@datatech.net.au, manager@client.com.au"
                spellCheck={false}
                style={{ width: "100%", padding: "7px 10px", borderRadius: 7, fontSize: 13,
                  border: `1px solid ${parseCc(ccText).invalid.length ? "var(--warn,#c0392b)" : "var(--border,#d0d0d0)"}` }} />
              {parseCc(ccText).invalid.length > 0 && (
                <div style={{ fontSize: 11, color: "var(--warn,#c0392b)", marginTop: 3 }}>Not a valid address: {parseCc(ccText).invalid.join(", ")}</div>
              )}
            </label>
            <div style={{ fontSize: 12, color: "var(--text3,#888)", paddingBottom: 8 }}>
              {cardCompanies.length} managed client{cardCompanies.length === 1 ? "" : "s"} · {periodLabel(period)}
              {/* Plan filter. "All" is the default so the page opens showing
                  everything that gets a report, rather than hiding two thirds of
                  the list behind a selection nobody made. */}
              <select
                style={{ marginLeft: 10, padding: "2px 6px", borderRadius: 6, fontSize: 11.5,
                  border: "1px solid var(--border,#d0d0d0)", background: "var(--card,#fff)",
                  color: "var(--text,#222)", fontFamily: "inherit" }}
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                title="Only Bronze, Silver and Gold clients receive a monthly report.">
                <option value="All">All plans</option>
                {REPORTED_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {/* ⚠ The count is cardCompanies.length — the SAME list rendered
                  below — so what the button says and what it generates cannot
                  disagree. Change the filter and both move together.
                  ⚠ Adhoc and Copper can never appear here: cardCompanies filters
                  to REPORTED_TIERS *before* the dropdown is applied, so they are
                  absent under "All plans" too. No skip logic is needed and none
                  is written; a test pins the absence instead. */}
              {cardCompanies.length > 0 && (
                <button
                  style={{ ...btn(false), marginLeft: 8, padding: "3px 10px", fontSize: 11.5 }}
                  disabled={!!(bulk && bulk.running) || generating || !period}
                  title={`Generate the report for every client shown under this filter (${periodLabel(period)})`}
                  onClick={doBulkGenerate}>
                  {bulk && bulk.running
                    ? `Generating ${bulk.done} of ${bulk.total}…`
                    : `Generate all (${cardCompanies.length} visible)`}
                </button>
              )}
              {bulk && bulk.running && (
                <button style={{ ...btn(false), marginLeft: 6, padding: "3px 9px", fontSize: 11.5, color: "var(--danger)" }}
                  onClick={() => { cancelBulkRef.current = true; }}
                  title="Finish the client in progress, then stop">
                  Stop
                </button>
              )}
              {statusLoading && " · refreshing…"}
            </div>
            {/* Did You Know? — ONE per month, carried by every client's report.
                It lives here rather than on the client cards because it is a
                property of the MONTH, not of a client; writing it once beside
                the month picker is the whole point of the change. */}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              <button
                style={{ ...btn(false), display: "flex", alignItems: "center", gap: 6 }}
                onClick={() => setDykOpen((o) => !o)}
                title="One cyber talking point, shared by every report for this month">
                💡 Did You Know?
                {dykSaved?.text
                  ? <span style={{ color: "var(--tact,#1baf7a)", fontWeight: 700 }}>· saved</span>
                  : <span style={{ color: "var(--text3,#999)" }}>· none yet</span>}
                <span style={{ color: "var(--text3,#999)" }}>{dykOpen ? "▲" : "▼"}</span>
              </button>
              <button style={btn(false)} onClick={() => loadStatus(period)}>↻ Refresh</button>
            </div>

            {dykOpen && (
              <div style={{ flexBasis: "100%", borderTop: "1px solid var(--border,#eee)", paddingTop: 10,
                            display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 11, color: "var(--text3,#888)" }}>
                  One paragraph for <strong>{periodLabel(period)}</strong>, shown on{" "}
                  <strong>every client's report</strong> for that month. Generate a draft, edit it,
                  then Save — nothing reaches a report until you do.
                </div>
                <textarea
                  value={dykText}
                  disabled={dykBusy}
                  onChange={(e) => setDykText(e.target.value)}
                  placeholder="Click Generate for an AI draft, then edit it here before saving…"
                  rows={5}
                  style={{ width: "100%", fontSize: 12, lineHeight: 1.55, padding: "8px 10px", borderRadius: 7,
                    border: "1px solid var(--border,#d0d0d0)", resize: "vertical", fontFamily: "inherit" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {/* Drafts reliably land in the 90s, which is fine copy — the
                      counter only nags past 100. */}
                  <span style={{ fontSize: 10.5, color: wordsOf(dykText) > 100 ? "var(--warn,#c0392b)" : "var(--text3,#999)" }}>
                    {wordsOf(dykText)} words{wordsOf(dykText) > 100 ? " · over target" : " · target 70–100"}
                  </span>
                  {dykTopic && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 5, background: "var(--bg,#f0f0f0)", color: "var(--text3,#777)" }}>{dykTopic}</span>}
                  {/* Unsaved-change marker: the textarea is a draft until Save,
                      and without this an edited-but-unsaved paragraph looks
                      identical to a stored one. */}
                  {dykSaved?.text && dykText.trim() !== dykSaved.text && (
                    <span style={{ fontSize: 10.5, color: "var(--warn,#c0392b)", fontWeight: 700 }}>· unsaved changes</span>
                  )}
                  <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    <button style={btn(false)} disabled={dykBusy} onClick={doDraftDyk}>
                      {dykBusy ? "…" : dykText ? "↻ Regenerate" : "✨ Generate"}
                    </button>
                    <button style={btn(true)} disabled={dykBusy || !dykText.trim()} onClick={doSaveDyk}>Save</button>
                    {dykSaved?.text && (
                      <button style={btn(false)} disabled={dykBusy}
                        title={`Remove it from every ${periodLabel(period)} report`}
                        onClick={doClearDyk}>✕ Remove</button>
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div style={{ fontSize: 12, color: "var(--text3,#888)", marginTop: -6 }}>
            Drop each client's PDFs on their card — <strong>Datto BCDR backup</strong> and <strong>Slipstream cyber</strong>.
            IT Health and WiFi are live from the Datto RMM and UniFi APIs, so they need no upload;
            their badges show whether the integration can see that client.
          </div>

          {cardCompanies.length === 0 && (
            <div style={{ ...card, fontSize: 13, color: "var(--text3,#888)" }}>
              No managed clients. Tick <strong>Mgd</strong> in Settings → Client Register to add clients here.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 12 }}>
            {cardCompanies.map((c) => {
              const cid = String(c.id);
              const st = statusFor(cid);
              const busy = busyCid === cid;
              const gen = genByCid[cid];
              const anySource = st.count > 0 || st.has.wifi;
              return (
                <div key={cid}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); handleFiles(cid, c.companyName, e.dataTransfer.files); }}
                  style={{ ...card, padding: 14, display: "flex", flexDirection: "column", gap: 9,
                    borderColor: busy ? "var(--dt-secondary,#0b80a5)" : "var(--border,#e2e2e2)" }}>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.25 }}>{c.companyName}</div>
                    <div style={{ fontSize: 10.5, color: "var(--text3,#999)" }}>
                      {st.unifiSiteName ? `UniFi: ${st.unifiSiteName}` : "UniFi: not mapped"}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {SOURCES.map((s) => <SourceBadge key={s.key} src={s} on={st.has[s.key]} />)}
                  </div>

                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: "auto" }}>
                    <input ref={(el) => { dropRef.current[cid] = el; }} type="file" accept="application/pdf" multiple
                      style={{ display: "none" }}
                      onChange={(e) => { handleFiles(cid, c.companyName, e.target.files); e.target.value = ""; }} />
                    <button style={{ ...btn(false), padding: "5px 9px" }} disabled={busy}
                      title="Upload this client's source PDFs for the selected month"
                      onClick={() => dropRef.current[cid] && dropRef.current[cid].click()}>
                      {busy ? "…" : "⬆ PDFs"}
                    </button>
                    <button style={{ ...btn(true), padding: "5px 11px" }} disabled={generating || busy}
                      onClick={() => doGenerate(cid)}>
                      {busy && generating ? "Generating…" : st.generated || gen ? "Regenerate" : "Generate"}
                    </button>
                    <button style={{ ...btn(false), padding: "5px 9px" }} title="Preview" onClick={() => doPreview(cid, period)}>👁</button>
                    <button style={{ ...btn(false), padding: "5px 9px" }} title="Copy share link" onClick={() => doShare(cid, period)}>🔗</button>
                    {pdfAvailable && <button style={{ ...btn(false), padding: "5px 9px" }} title="Download PDF" onClick={() => doPdf(cid, period)}>⬇</button>}
                    <button style={{ ...btn(false), padding: "5px 9px" }} title={recipients[cid] ? `Email the report link to ${recipients[cid]}` : "Email the report link to the client"}
                      onClick={() => openEmailDialog(cid)}>✉ Email</button>
                  </div>
                  <div style={{ fontSize: 10.5, color: recipients[cid] ? "var(--text3,#888)" : "var(--text3,#b0b0b0)" }}
                    title="The client's report recipient — edit it in the Email dialog; it is remembered for next month">
                    ✉ {recipients[cid] || "No recipient set yet"}
                  </div>

                  {!anySource && (
                    <div style={{ fontSize: 10.5, color: "var(--text3,#a0a0a0)" }}>
                      No sources yet — the report will show placeholders, not zeros.
                    </div>
                  )}

                  <button
                    style={{ ...btn(false), padding: "3px 8px", fontSize: 10.5, textAlign: "left" }}
                    onClick={() => openCredentials(cid, c.companyName)}
                    title="Named password + MFA status per mailbox. Confidential — never goes on the client's report link.">
                    🔐 Credential hygiene
                  </button>

                  {(() => {
                    const d = discuss.find((x) => String(x.companyId) === String(cid));
                    if (!d) return null;
                    return (
                      <div
                        style={{ padding: "5px 8px", fontSize: 10.5, borderRadius: 6, marginBottom: 2,
                          background: d.unread ? "#e3f2f9" : "var(--s2,#f6f6f6)",
                          border: `1px solid ${d.unread ? "#16b0e8" : "var(--border,#e2e2e2)"}`,
                          color: "#0b6a8a", fontWeight: 600, cursor: "default" }}
                        title={`${d.sections.join("\n")}\n\nFrom the ${d.period} report. These appear on the next Monthly Tech Catchup prep.`}
                        onMouseEnter={() => {
                          // Hovering IS reading it — clears the unread state without
                          // marking the questions covered. Those are different things:
                          // "covered" lives on the prep, where the answering happens.
                          if (!d.unread) return;
                          markDiscussSeen(d.companyId, d.period).catch(() => {});
                          setDiscuss((prev) => prev.map((x) =>
                            (x.companyId === d.companyId && x.period === d.period ? { ...x, unread: false } : x)));
                        }}>
                        🔔 Client flagged {d.count} section{d.count !== 1 ? "s" : ""} to discuss
                      </div>
                    );
                  })()}

                  <button
                    style={{ ...btn(false), padding: "3px 8px", fontSize: 10.5, textAlign: "left" }}
                    onClick={() => setNotesFor({ companyId: cid, companyName: c.companyName })}
                    title="vCIO commentary pinned to a report section. Client-facing — it appears on their report.">
                    📝 Section notes
                  </button>

                  <button
                    style={{ ...btn(false), padding: "3px 8px", fontSize: 10.5, textAlign: "left" }}
                    onClick={() => setRoadmapFor({ companyId: cid, companyName: c.companyName })}
                    title="Budgeted projects and approved recommendations — the report's last section. Client-facing.">
                    🗺️ Roadmap
                  </button>

                </div>
              );
            })}
          </div>
        </>
      )}

      {view === "intake" && (
        <>
          <div style={{ ...card, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Inbound reports</div>
              <div style={{ fontSize: 11, color: "var(--muted,#777)", marginTop: 3 }}>
                Vendor PDFs emailed to{" "}
                <strong>{intakeInfo?.mailbox || "the intake mailbox"}</strong>{" "}
                that could not be filed with confidence. Nothing here has been guessed at —
                confirm the client and it files exactly as a manual upload would.
              </div>
              {intakeInfo?.lastRunAt && (
                <div style={{ fontSize: 11, color: "var(--muted,#999)", marginTop: 3 }}>
                  Last checked {new Date(intakeInfo.lastRunAt).toLocaleString()}
                  {intakeInfo.senderAllowlist?.length
                    ? ` · trusted senders: ${intakeInfo.senderAllowlist.join(", ")}`
                    : " · no trusted senders set, so everything is reviewed"}
                </div>
              )}
            </div>
            <button style={btn(true)} onClick={checkMailNow} disabled={intakeLoading}>
              {intakeLoading ? "Checking…" : "↻ Check mailbox now"}
            </button>
          </div>

          {intakeLoading && !intake && <div style={{ fontSize: 12, color: "var(--muted,#777)" }}>Loading…</div>}

          {intake && intake.length === 0 && (
            <div style={{ ...card, textAlign: "center", color: "var(--muted,#777)", fontSize: 13, padding: 28 }}>
              Nothing waiting. Reports that arrive by email and identify themselves clearly
              are filed automatically; only the uncertain ones stop here.
            </div>
          )}

          {(intake || []).map((it) => {
            const e = edits[it.hash] || {};
            const busy = intakeBusy === it.hash;
            return (
              <div key={it.hash} style={{ ...card, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, wordBreak: "break-word" }}>{it.fileName}</div>
                    <div style={{ fontSize: 11, color: "var(--muted,#777)", marginTop: 2 }}>
                      From {it.sender || "unknown"}
                      {it.subject ? ` · "${it.subject}"` : ""}
                      {it.receivedAt ? ` · ${new Date(it.receivedAt).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  <span style={{ background: "#fff4e2", color: "#b7791f", borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {it.reason}
                  </span>
                </div>

                {it.detail && (
                  <div style={{ fontSize: 12, background: "var(--bg2,#fafafa)", borderLeft: "3px solid #b7791f", padding: "7px 10px", borderRadius: 4 }}>
                    {it.detail}
                  </div>
                )}

                {/* What the PDF says about itself — the evidence behind the guess. */}
                {(it.vendorClientString || it.candidates?.length > 0) && (
                  <div style={{ fontSize: 11, color: "var(--muted,#777)" }}>
                    {it.vendorClientString && <>PDF names itself <strong>&ldquo;{it.vendorClientString}&rdquo;</strong>. </>}
                    {it.candidates?.length > 1 && <>Matches {it.candidates.length} clients: {it.candidates.map((c) => c.companyName).join(", ")}. </>}
                    {it.candidates?.length === 0 && <>No Autotask client matched — set this client&rsquo;s vendor identity in the Client Register so it files itself next month. </>}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <select value={e.companyId || ""} onChange={(ev) => patchEdit(it.hash, "companyId", ev.target.value)}
                    style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--border,#ddd)", fontSize: 12, minWidth: 210 }}>
                    <option value="">— pick the client —</option>
                    {[...companies].sort((a, b) => String(a.companyName).localeCompare(String(b.companyName)))
                      .map((c) => <option key={c.id} value={String(c.id)}>{c.companyName}</option>)}
                  </select>
                  <select value={e.type || ""} onChange={(ev) => patchEdit(it.hash, "type", ev.target.value)}
                    style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--border,#ddd)", fontSize: 12 }}>
                    <option value="">— type —</option>
                    <option value="datto-bcdr">Datto BCDR</option>
                    <option value="slipstream-cyber">Cyber</option>
                    <option value="dark-web">Dark Web</option>
                    <option value="3cx-call-report">3CX Call Report</option>
                  </select>
                  <input type="month" value={e.period || ""} onChange={(ev) => patchEdit(it.hash, "period", ev.target.value)}
                    style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--border,#ddd)", fontSize: 12 }} />

                  {it.pendingBlob && (
                    <a href={getIntakePdfUrl(it.hash)} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: "var(--accent,#16b0e8)", fontWeight: 600, textDecoration: "none" }}>
                      View file ↗
                    </a>
                  )}

                  <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    <button style={btn(true)} disabled={busy} onClick={() => confirmIntake(it)}>
                      {busy ? "Filing…" : "Confirm"}
                    </button>
                    <button style={btn(false)} disabled={busy} onClick={() => rejectIntake(it)}>Reject</button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Craig's free-form notice under the Exec Summary — global and/or per
          client. Scoped to cardCompanies (the clients that actually receive a
          report) so the picker cannot offer a notice to someone who gets none. */}
      {view === "notices" && (
        <ReportNotices
          companies={cardCompanies}
          onMessage={(m) => toast(m.text, m.kind)}
        />
      )}

      {view === "archive" && (
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>Report archive</div>
            <button style={{ ...btn(false), marginLeft: "auto" }} onClick={loadArchive}>↻ Refresh</button>
          </div>
          {archive?.unifiPending && <div style={{ fontSize: 11, color: "var(--text3,#888)", marginBottom: 10 }}>UniFi site names populate once the UniFi integration is wired.</div>}
          {archiveLoading && <div style={{ fontSize: 13, color: "var(--text3,#888)" }}>Loading…</div>}
          {!archiveLoading && archive && archive.years?.length === 0 && <div style={{ fontSize: 13, color: "var(--text3,#888)" }}>Nothing archived yet — generate a report to populate this.</div>}
          {!archiveLoading && archive?.years?.map((y) => (
            <div key={y.year} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{y.year}</div>
              {y.months.map((mo) => (
                <div key={mo.period} style={{ marginBottom: 10, paddingLeft: 10, borderLeft: "3px solid var(--dt-secondary,#0b80a5)" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2,#555)", marginBottom: 6 }}>{mo.label}</div>
                  {mo.clients.map((cl) => (
                    <div key={cl.companyId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: "var(--bg,#f6f6f6)", marginBottom: 5, flexWrap: "wrap" }}>
                      <div style={{ minWidth: 180, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{cl.companyName}</div>
                        <div style={{ fontSize: 10, color: "var(--text3,#999)" }}>UniFi: {cl.unifiSiteName || "—"}</div>
                      </div>
                      <div style={{ display: "flex", gap: 4 }}>{(cl.types || []).map((t) => <TypeBadge key={t} t={t} />)}</div>
                      <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                        <button style={btn(false)} onClick={() => doPreview(cl.companyId, mo.period, `${cl.companyName} — ${mo.label}`)}>Preview</button>
                        <button style={btn(false)} onClick={() => doShare(cl.companyId, mo.period)}>🔗</button>
                        {pdfAvailable && <button style={btn(false)} onClick={() => doPdf(cl.companyId, mo.period)}>⬇</button>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {credOpen && (
        <div onClick={() => { setCredOpen(null); setCredLink(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 650, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--card,#fff)", borderRadius: 12, width: 880, maxWidth: "100%", maxHeight: "86vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border,#eee)" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🔐 Credential hygiene — {credOpen.companyName}</div>
              <div style={{ fontSize: 11, color: "var(--warn,#c0392b)", marginTop: 2 }}>
                Confidential — names individuals. The client&apos;s report shows counts only; this table never goes on the report link.
              </div>
            </div>
            <div style={{ padding: 16, overflow: "auto", flex: 1 }}>
              {credBusy && <div style={{ fontSize: 12, color: "var(--text3,#888)" }}>Working… checking every mailbox against Microsoft 365 and IT Glue.</div>}
              {!credBusy && !credOpen.data && (
                <div style={{ fontSize: 12.5 }}>
                  Nothing computed for {periodLabel(period)} yet.
                  <div style={{ marginTop: 10 }}>
                    <button style={btn(true)} onClick={() => runCredentialBuild(credOpen.companyId, credOpen.companyName)}>Run credential check</button>
                  </div>
                </div>
              )}
              {!credBusy && credOpen.data && <CredTable data={credOpen.data} />}
              {credLink && (
                <div style={{ marginTop: 14, fontSize: 11.5, background: "var(--bg,#f6f6f6)", padding: "10px 12px", borderRadius: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Expiring link — expires {new Date(credLink.expiresAt).toLocaleString()}</div>
                  <div style={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: 10.5 }}>{credLink.url}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <button style={{ ...btn(false), padding: "4px 9px", fontSize: 11 }} onClick={() => { navigator.clipboard?.writeText(credLink.url); toast("Link copied."); }}>Copy</button>
                    <button style={{ ...btn(false), padding: "4px 9px", fontSize: 11 }}
                      onClick={() => revokeCredentialLink(credLink.jti).then(() => { setCredLink(null); toast("Link revoked."); })}>Revoke now</button>
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border,#eee)", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={btn(false)} disabled={credBusy} onClick={() => runCredentialBuild(credOpen.companyId, credOpen.companyName)}>↻ Re-run check</button>
              {credOpen.data && (
                <button style={btn(false)} disabled={credBusy}
                  onClick={() => createCredentialLink(credOpen.companyId, period, 24)
                    .then((r) => { setCredLink(r); toast("Expiring link created (24h)."); })
                    .catch((e) => toast(e?.response?.data?.error || e.message || "Could not create link", "err"))}>
                  🔗 Create 24h link
                </button>
              )}
              <button style={{ ...btn(false), marginLeft: "auto" }} onClick={() => { setCredOpen(null); setCredLink(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {roadmapFor && (
        <RoadmapModal
          companyId={roadmapFor.companyId}
          companyName={roadmapFor.companyName}
          period={period}
          periodLabel={periodLabel(period)}
          onClose={() => setRoadmapFor(null)}
          toast={(msg, kind) => toast(msg, kind)}
          onCreateQuote={onCreateQuote}
        />
      )}

      {notesFor && (
        <SectionNotesModal
          companyId={notesFor.companyId}
          companyName={notesFor.companyName}
          period={period}
          periodLabel={periodLabel(period)}
          onClose={() => setNotesFor(null)}
          toast={(msg, kind) => toast(msg, kind)}
        />
      )}

      {emailDlg && (
        /* Confirm-before-send. It goes to a real client, so Craig sees To, CC,
           subject, the exact wording and the link before anything leaves. */
        <div onClick={() => !emailDlg.sending && setEmailDlg(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 620, display: "grid", placeItems: "center", padding: 14 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--card,#fff)", borderRadius: 12, width: "100%", maxWidth: 640, boxShadow: "0 12px 40px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border,#eee)" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Email report link — {emailDlg.name}</div>
              <div style={{ fontSize: 11.5, color: "var(--text3,#888)", marginTop: 2 }}>{periodLabel(period)} · sent from craig.grant@datatech.net.au · replies go to Craig</div>
            </div>
            <div style={{ padding: "14px 18px", overflow: "auto", display: "flex", flexDirection: "column", gap: 12, fontSize: 13 }}>
              {emailDlg.loading && <div style={{ color: "#888" }}>Preparing the email…</div>}
              {!emailDlg.loading && emailDlg.draft && !emailDlg.sent && (() => {
                const cc = parseCc(ccText);
                const toOk = isEmail(String(emailDlg.to || "").trim());
                return (
                  <>
                    <label style={{ fontSize: 12 }}>
                      <div style={{ color: "var(--text3,#888)", marginBottom: 4, fontWeight: 600 }}>To <span style={{ fontWeight: 400 }}>{emailDlg.test ? "(test send — NOT remembered as the client's recipient)" : "(remembered for this client after a successful send)"}</span></div>
                      <input type="email" value={emailDlg.to} autoFocus spellCheck={false}
                        onChange={(e) => setEmailDlg((d) => ({ ...d, to: e.target.value, error: null }))}
                        placeholder="client@theirdomain.com.au"
                        style={{ width: "100%", padding: "7px 10px", borderRadius: 7, fontSize: 13, border: `1px solid ${emailDlg.to && !toOk ? "var(--warn,#c0392b)" : "var(--border,#d0d0d0)"}` }} />
                    </label>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ color: "var(--text3,#888)", marginBottom: 2, fontWeight: 600 }}>CC</div>
                      {cc.valid.length ? cc.valid.join(", ") : <span style={{ color: "var(--text3,#aaa)" }}>none — the CC field at the top of the page is empty</span>}
                      {cc.invalid.length > 0 && <div style={{ color: "var(--warn,#c0392b)", marginTop: 2 }}>Invalid CC entry: {cc.invalid.join(", ")} — fix the CC field before sending.</div>}
                    </div>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ color: "var(--text3,#888)", marginBottom: 2, fontWeight: 600 }}>Subject</div>
                      <div style={{ fontWeight: 600 }}>
                        {emailDlg.test && <span style={{ background: "#fdf3e2", border: "1px solid #f0d6a6", color: "#8f5405", borderRadius: 5, padding: "1px 6px", marginRight: 6, fontSize: 11 }}>TEST</span>}
                        {emailDlg.draft.subject}
                      </div>
                    </div>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ color: "var(--text3,#888)", marginBottom: 4, fontWeight: 600 }}>Message</div>
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 12.5, lineHeight: 1.5, background: "var(--bg,#f6f6f6)", padding: "10px 12px", borderRadius: 8 }}>{emailDlg.draft.text}</pre>
                    </div>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ color: "var(--text3,#888)", marginBottom: 2, fontWeight: 600 }}>Link in the email</div>
                      <a href={emailDlg.draft.shareUrl} target="_blank" rel="noreferrer" style={{ wordBreak: "break-all" }}>{emailDlg.draft.shareUrl}</a>
                      <div style={{ color: "var(--text3,#999)", marginTop: 2 }}>Link only — the PDF is not attached; the share page has its own Export button.</div>
                    </div>
                  </>
                );
              })()}
              {emailDlg.sent && (
                <div style={{ background: "#e9f6ee", border: "1px solid #bcdfcb", color: "#00631f", borderRadius: 8, padding: "10px 12px" }}>
                  ✓ Sent to <strong>{emailDlg.sent.to}</strong>{emailDlg.sent.cc?.length ? <> · cc {emailDlg.sent.cc.join(", ")}</> : null}
                  <div style={{ fontSize: 11.5, marginTop: 2 }}>{emailDlg.sent.subject}</div>
                </div>
              )}
              {emailDlg.error && (
                <div role="alert" style={{ background: "#fdecea", border: "1px solid #f2c6c2", color: "#a32a24", borderRadius: 8, padding: "10px 12px" }}>{emailDlg.error}</div>
              )}
            </div>
            <div style={{ padding: "10px 18px", borderTop: "1px solid var(--border,#eee)", display: "flex", gap: 8, alignItems: "center" }}>
              {!emailDlg.sent && (
                /* Test send: [TEST]-prefixed subject + marker line, and the To
                   is not stored as the client's recipient. For sending a copy
                   to yourself before the real one goes. */
                <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, marginRight: "auto", cursor: emailDlg.sending ? "default" : "pointer" }}>
                  <input type="checkbox" checked={!!emailDlg.test} disabled={emailDlg.sending} onChange={(e) => toggleEmailTest(e.target.checked)} />
                  Test send <span style={{ color: "var(--text3,#888)" }}>— marks it [TEST], won't save the recipient</span>
                </label>
              )}
              <button style={{ ...btn(false), marginLeft: emailDlg.sent ? "auto" : 0 }} disabled={emailDlg.sending} onClick={() => setEmailDlg(null)}>{emailDlg.sent ? "Close" : "Cancel"}</button>
              {!emailDlg.sent && (
                <button style={{ ...btn(true), opacity: emailDlg.sending ? 0.7 : 1 }}
                  disabled={emailDlg.sending || emailDlg.loading || !emailDlg.draft || !isEmail(String(emailDlg.to || "").trim()) || parseCc(ccText).invalid.length > 0}
                  onClick={doSendEmail}>
                  {emailDlg.sending ? "Sending…" : emailDlg.test ? "Send TEST email" : "Send email"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {typePrompt && (
        <div onClick={() => !typeBusy && setTypePrompt(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 610, display: "grid", placeItems: "center", padding: 14 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 12, maxWidth: 620, width: "100%", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid #eee" }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>
                Which report is this? — {typePrompt.companyName}
              </div>
              <button style={{ ...btn(false), marginLeft: "auto" }} disabled={typeBusy}
                      onClick={() => setTypePrompt(null)}>✕ Close</button>
            </div>
            <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
              {typePrompt.items.map((it, i) => (
                <div key={i} style={{ border: "1px solid #eee", borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{it.file.name}</div>
                  <div style={{ fontSize: 11, color: "#777", marginBottom: 8 }}>
                    {it.error || "The type could not be read from the document."}
                  </div>
                  <select
                    value={it.choice}
                    disabled={typeBusy}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTypePrompt((p) => ({
                        ...p, items: p.items.map((x, j) => (j === i ? { ...x, choice: v } : x)),
                      }));
                    }}
                    style={{ width: "100%", padding: "6px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #ccc" }}
                  >
                    <option value="">— Skip this file —</option>
                    {it.types.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              ))}
              <div style={{ fontSize: 11, color: "#777" }}>
                Naming the type does not override the client checks — a PDF naming a
                different client is still refused.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={btn(true)} disabled={typeBusy || !typePrompt.items.some((it) => it.choice)}
                        onClick={submitTypeChoices}>
                  {typeBusy ? "Filing…" : "File selected"}
                </button>
                <button style={btn(false)} disabled={typeBusy}
                        onClick={() => setTypePrompt(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(preview || previewLoading) && (
        /* ⚠ TOP STARTS AT THE HEADER, NOT AT 0 — and the reason is a stacking
            context, not a z-index that is too low. `.body` in MspOsApp is
            `position:relative; z-index:1`, which CREATES A STACKING CONTEXT, so
            this overlay's z-index 600 is resolved INSIDE that layer and cannot
            escape it. The app header sits in the parent context at z-index 20,
            and 1 < 20, so the whole overlay paints BENEATH the header however
            high its own z-index goes. With `inset: 0` it spanned the full
            viewport and the header simply covered its top 46px, hiding the top
            of the report card.
            Raising the z-index would NOT fix it. Starting below the header does,
            and it also gives Craig what he asked for: the header stays visible
            and the report sits under it rather than behind it.
            ⚠ var(--hdr-h), never a literal 46 — the header owns that number. */
        <div onClick={() => setPreview(null)}
          style={{ position: "fixed", top: "var(--hdr-h)", left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", flexDirection: "column", padding: 14 }}>
          {/* Full-width viewer: the report body is 1180px, so anything narrower
              forced it to shrink and read as a cramped column. */}
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", maxWidth: 1240, width: "100%", margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid #eee" }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{previewLoading ? "Rendering…" : preview?.title}</div>
              {previewPdfError && (
                <div role="alert" style={{ marginLeft: "auto", fontSize: 12, color: "var(--warn,#c0392b)", maxWidth: 420 }}>
                  PDF export failed: {previewPdfError}
                </div>
              )}
              {pdfAvailable && preview && (
                <button style={{ ...btn(true), marginLeft: previewPdfError ? 0 : "auto", opacity: previewPdfBusy ? 0.6 : 1, cursor: previewPdfBusy ? "wait" : "pointer" }}
                  disabled={previewPdfBusy} aria-busy={previewPdfBusy}
                  title="Download this report as the client PDF" onClick={doPreviewPdf}>
                  {previewPdfBusy ? "⏳ Building PDF…" : "⬇ Export to PDF"}
                </button>
              )}
              <button style={{ ...btn(false), marginLeft: (pdfAvailable && preview) || previewPdfError ? 0 : "auto" }} onClick={() => setPreview(null)}>✕ Close</button>
            </div>
            {previewLoading
              ? <div style={{ flex: 1, display: "grid", placeItems: "center", color: "#888" }}>Building preview…</div>
              : <iframe title="report-preview" srcDoc={preview?.html} style={{ flex: 1, border: "none", width: "100%" }} />}
          </div>
        </div>
      )}
    </div>
  );
}
